import {
  ApplicationStatus,
  Typenames,
  VLocApplicationStatusExistenceError,
  VLocApplicationStatusSuccess,
} from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import { useEffect, useRef, useState } from 'react';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';

export const MAX_ATTEMPTS = 12;
export const POLL_INTERVAL = 5000;

const useHardCheckPollingEffect = (
  hardCheckResult:
    | VLocApplicationStatusExistenceError
    | VLocApplicationStatusSuccess
    | undefined,
  refetchCheck: () => void,
  tierId: string,
) => {
  const router = useRouter();
  const { segmentClient } = useAnalyticsConfiguration();

  const [isChecking, setIsChecking] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [showError, setShowError] = useState(false);
  const hasLoggedEvent = useRef(false);

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const clearPollingInterval = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const handleHardCheckResult = () => {
      if (
        hardCheckResult &&
        hardCheckResult.__typename === Typenames.VLocApplicationStatusSuccess &&
        hardCheckResult.applicationStatus !== ApplicationStatus.Pending
      ) {
        setIsChecking(false);
        clearPollingInterval();

        if (hardCheckResult.applicationStatus === ApplicationStatus.Approved) {
          if (!hasLoggedEvent.current) {
            hasLoggedEvent.current = true;
            void segmentClient?.logEvent('VLOC Application Approved', {
              category: 'vloc',
              subcategory: 'vloc_application',
            });
          }
          const approvedPageCopy = hardCheckResult.approvedPageCopy;
          router.push({
            pathname: '/varo-line-of-credit/hard-pull-approved',
            params: {
              approvedTitle: approvedPageCopy?.title,
              approvedCongratsText: approvedPageCopy?.congratsText,
              approvedDescription: approvedPageCopy?.description,
              approvedAdditionalQualifications:
                approvedPageCopy?.additionalQualifications,
              tierId: tierId,
            },
          });
        } else if (
          hardCheckResult.applicationStatus === ApplicationStatus.Declined
        ) {
          if (!hasLoggedEvent.current) {
            hasLoggedEvent.current = true;
            void segmentClient?.logEvent('VLOC Application Declined', {
              category: 'vloc',
              subcategory: 'vloc_application',
            });
          }
          router.push({
            pathname: '/varo-line-of-credit/line-of-credit-entry-point',
          });
        }
      }
    };

    if (isChecking) {
      if (attempts === 0) {
        refetchCheck();
        handleHardCheckResult();
        setAttempts((prev) => prev + 1);
      }
      if (attempts < MAX_ATTEMPTS) {
        intervalId = setInterval(() => {
          refetchCheck();
          setAttempts((prev) => prev + 1);
        }, POLL_INTERVAL);
      }
      if (attempts >= MAX_ATTEMPTS) {
        clearPollingInterval();
        setShowError(true);
      }
      handleHardCheckResult();
    }

    return () => {
      clearPollingInterval();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChecking, attempts, hardCheckResult, refetchCheck, tierId, router]);

  return {
    isChecking,
    attempts,
    showError,
    setIsChecking,
    setAttempts,
    setShowError,
  };
};

export default useHardCheckPollingEffect;
